import { TAGS_MAPPING, TypeTagsKey } from '@shared/api/middleware/mappingAPI/city/tagsMapping'

/**
 * @function getQuickPromptTag - функция для получения prompt_tag для ассистента
 * @description quickPromptsMapping = { quick_filter_tag: { preview_tag, prompt_tag } }, где:
 *  - preview_tag - версия для отображения в кнопке быстрого промпта
 *  - prompt_tag - версия для непосредственного использования в запросе
 */
export type QuickPromptMappingItem = {
  latin_slug: string
  preview_tag: string
  prompt_tag: string
}

/**
 * @constant quickPromptsMapping - маппинг тегов для быстрого промпта
 *
 * @value preview_tag - версия для отображения в кнопке быстрого промпта
 * @value prompt_tag - версия для непосредственного использования в запросе промпта
 *
 * @example 1. для ключа "Паназиатская кухня"
 *    - на кнопках стартового окна будет отображаться значение поля preview_tag
 *      "Где найти паназиатскую кухню?"
 *    - а в сообщении запроса будет значение поля prompt_tag
 *      "Где найти рестораны и кафе с паназиатской кухней?"
 *
 * @todo переписать маппинг с ключей кириллицы, на значения поля latin_slug
 */
export const quickPromptsMapping: Record<TypeTagsKey, QuickPromptMappingItem> = {
  Бары: {
    latin_slug: 'bary',
    preview_tag: 'Где найти бары?',
    prompt_tag: 'Где найти бары?',
  },
  Кофейни: {
    latin_slug: 'kofeyni',
    preview_tag: 'Где найти кофейни?',
    prompt_tag: 'Где найти кофейни?',
  },
  Кондитерские: {
    latin_slug: 'konditerskie',
    preview_tag: 'Где найти кондитерские?',
    prompt_tag: 'Где найти кондитерские кафе?',
  },
  Бургеры: {
    latin_slug: 'burgery',
    preview_tag: 'Где попробовать бургеры?',
    prompt_tag: 'Где найти заведения с бургерами?',
  },
  Стейки: {
    latin_slug: 'steyki',
    preview_tag: 'Где попробовать стейки?',
    prompt_tag: 'Где найти заведения с стейками?',
  },
  Рыба: {
    latin_slug: 'ryba',
    preview_tag: 'Где попробовать рыбу?',
    prompt_tag: 'Где найти заведения с рыбой?',
  },
  Завтраки: {
    latin_slug: 'zavtraki',
    preview_tag: 'Где найти завтраки?',
    prompt_tag: 'Где найти заведения с завтраками?',
  },
  'Бизнес-ланч': {
    latin_slug: 'biznes_lanch',
    preview_tag: 'Где найти бизнес-ланч?',
    prompt_tag: 'Где найти заведения с бизнес-ланчем?',
  },
  'У воды': {
    latin_slug: 'u_vody',
    preview_tag: 'Где поесть у воды?',
    prompt_tag: 'Где найти заведения у воды?',
  },
  'С событиями': {
    latin_slug: 's_sobytiyami',
    preview_tag: 'Где заведения с событиями?',
    prompt_tag: 'Где найти заведения с событиями?',
  },
  'Пивные рестораны': {
    latin_slug: 'pivnye_restorany',
    preview_tag: 'Где пивные рестораны?',
    prompt_tag: 'Где найти пивные рестораны и бары?',
  },
  'Европейская кухня': {
    latin_slug: 'evropeyskaya_kuhnya',
    preview_tag: 'Где европейская кухня?',
    prompt_tag: 'Где найти рестораны и кафе с европейской кухней?',
  },
  'Грузинская кухня': {
    latin_slug: 'gruzinskaya_kuhnya',
    preview_tag: 'Где грузинская кухня?',
    prompt_tag: 'Где найти рестораны и кафе с грузинской кухней?',
  },
  'Итальянская кухня': {
    latin_slug: 'italyanskaya_kuhnya',
    preview_tag: 'Где итальянская кухня?',
    prompt_tag: 'Где найти рестораны и кафе с итальянской кухней?',
  },
  'Паназиатская кухня': {
    latin_slug: 'panaziatskaya_kuhnya',
    preview_tag: 'Где паназиатская кухня?',
    prompt_tag: 'Где найти рестораны и кафе с паназиатской кухней?',
  },
  'Узбекская кухня': {
    latin_slug: 'uzbekskaya_kuhnya',
    preview_tag: 'Где узбекская кухня?',
    prompt_tag: 'Где найти рестораны и кафе с узбекской кухней?',
  },
  'Русская кухня': {
    latin_slug: 'russkaya_kuhnya',
    preview_tag: 'Где русская кухня?',
    prompt_tag: 'Где найти рестораны и кафе с русской кухней?',
  },
  'Татарская кухня': {
    latin_slug: 'tatarskaya_kuhnya',
    preview_tag: 'Где татарская кухня?',
    prompt_tag: 'Где найти рестораны и кафе с татарской кухней?',
  },
  'Японская кухня': {
    latin_slug: 'yaponskaya_kuhnya',
    preview_tag: 'Где японская кухня?',
    prompt_tag: 'Где найти рестораны и кафе с японской кухней?',
  },
  'Американская кухня': {
    latin_slug: 'amerikanskaya_kuhnya',
    preview_tag: 'Где американская кухня?',
    prompt_tag: 'Где найти рестораны и кафе с американской кухней?',
  },
  'Башкирская кухня': {
    latin_slug: 'bashkirskaya_kuhnya',
    preview_tag: 'Где башкирская кухня?',
    prompt_tag: 'Где найти рестораны и кафе с башкирской кухней?',
  },
}

/**
 * @function getQuickPromptTag - функция для получения preview_tag для отображения в кнопке быстрого промпта
 */
export const getQuickPromptTag = (tag: TypeTagsKey): string => {
  return quickPromptsMapping[tag]?.preview_tag || tag
}

/**
 * @function getQuickPromptTagForRequest - функция для получения prompt_tag для запроса к ассистенту
 */
export const getQuickPromptTagForRequest = (tag: TypeTagsKey): string => {
  return quickPromptsMapping[tag]?.prompt_tag || tag
}

/**
 * @function getAssistTags - функция для получения случайных тегов для ассистента
 * @deprecated вместо нее используется getAssistTagsWithRequestData для получения данных с разделением отображения и запроса
 */
export const getAssistTags = (): string[] => {
  const getRandomTag = (): TypeTagsKey => {
    const keys = Object.keys(quickPromptsMapping) as TypeTagsKey[]
    const randomIndex = Math.floor(Math.random() * keys.length)
    return keys[randomIndex]
  }

  const currentTag = getRandomTag()
  const declinedTag = getQuickPromptTag(currentTag)
  const dynamicTag = `${declinedTag}`

  return ['Что ты умеешь?', dynamicTag, 'Рестораны с бронированием']
}

/**
 * @function getAssistTagsWithRequestData - функция для получения тегов с данными для запроса
 * @returns {Array<{display: string, request: string}>} массив объектов с отображаемым текстом и текстом для запроса
 */
export const getAssistTagsWithRequestData = (): Array<{ display: string; request: string }> => {
  const getRandomTag = (): TypeTagsKey => {
    const keys = Object.keys(quickPromptsMapping) as TypeTagsKey[]
    const randomIndex = Math.floor(Math.random() * keys.length)
    return keys[randomIndex]
  }

  const currentTag = getRandomTag()
  const displayTag = getQuickPromptTag(currentTag)
  const requestTag = getQuickPromptTagForRequest(currentTag)

  return [
    { display: 'Что ты умеешь?', request: 'Что ты умеешь?' },
    { display: displayTag, request: requestTag },
    { display: 'Рестораны с бронированием', request: 'Где рестораны и кафе с онлайн-бронированием?' },
  ]
}
